import { api } from './api';

export type JobStatus = 'APPLIED' | 'HR' | 'TECH' | 'OFFER' | 'REJECTED';

export interface Job {
  id: string;
  company: string;
  role: string;
  status: JobStatus;
  notes?: string;
  jobUrl?: string;
  appliedAt: string;
  createdAt: string;
  updatedAt: string;
  interviews?: InterviewAnalysis[];
  documents?: ApplicationDocument[];
}

export interface ApplicationDocument {
  id: string;
  fileName: string;
  mimeType?: string;
  fileSize: number;
  createdAt: string;
  updatedAt: string;
}

export type InterviewStatus = 'pending' | 'processing' | 'done' | 'failed';

export interface InterviewAnalysis {
  id: string;
  title: string;
  fileName: string;
  mimeType?: string;
  transcript?: string | null;
  transcriptTimeline?: TranscriptTimelineEntry[] | null;
  summary?: string | null;
  score?: number | null;
  strengths: string[];
  improvements: string[];
  status: InterviewStatus;
  errorMessage?: string | null;
  interviewType?: string;
  intervieweeSpeaker?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TranscriptTimelineEntry {
  speaker: string;
  startMs: number;
  endMs: number;
  startLabel: string;
  endLabel: string;
  text: string;
  isInterviewee?: boolean;
  suggestion?: string;
}

export interface JobStats {
  total: number;
  interviews: number;
  offers: number;
  conversionRate: number;
}

export interface CreateJobDto {
  company: string;
  role: string;
  status?: JobStatus;
  notes?: string;
  jobUrl?: string;
  appliedAt?: string;
}

export interface UploadInterviewDto {
  file: File;
}

export interface UploadApplicationDocumentsDto {
  files: File[];
}

export const jobsService = {
  findAll: () => api.get<Job[]>('/jobs'),
  findOne: (id: string) => api.get<Job>(`/jobs/${id}`),
  getStats: () => api.get<JobStats>('/jobs/stats'),
  create: (dto: CreateJobDto) => api.post<Job>('/jobs', dto),
  update: (id: string, dto: Partial<CreateJobDto>) => api.patch<Job>(`/jobs/${id}`, dto),
  remove: (id: string) => api.delete<void>(`/jobs/${id}`),
  listInterviews: (jobId: string) => api.get<InterviewAnalysis[]>(`/jobs/${jobId}/interviews`),
  deleteInterview: (jobId: string, interviewId: string) =>
    api.delete<void>(`/jobs/${jobId}/interviews/${interviewId}`),
  getInterviewVideoUrl: (jobId: string, interviewId: string) =>
    api.get<string>(`/jobs/${jobId}/interviews/${interviewId}/video-url`),
  getInterview: (jobId: string, interviewId: string) =>
    api.get<InterviewAnalysis>(`/jobs/${jobId}/interviews/${interviewId}`),
  uploadInterview: async (
    jobId: string,
    dto: UploadInterviewDto,
    onProgress?: (pct: number) => void,
    signal?: AbortSignal,
  ) => {
    const presign = await api.post<{
      uploadUrl: string;
      interviewId: string;
      s3Key: string;
    }>(`/jobs/${jobId}/interviews/presign`, {
      fileName: dto.file.name,
      mimeType: dto.file.type || 'application/octet-stream',
    });

    if (signal?.aborted) throw new DOMException('Upload cancelled', 'AbortError');

    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', presign.uploadUrl);
      xhr.setRequestHeader('Content-Type', dto.file.type || 'application/octet-stream');
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`S3 upload failed with ${xhr.status}`));
      };
      xhr.onerror = () => reject(new Error('S3 upload network error'));
      xhr.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'));
      if (signal) {
        signal.addEventListener('abort', () => xhr.abort(), { once: true });
      }
      xhr.send(dto.file);
    });

    if (signal?.aborted) throw new DOMException('Upload cancelled', 'AbortError');

    return api.post<InterviewAnalysis>(
      `/jobs/${jobId}/interviews/${presign.interviewId}/confirm`,
      {},
    );
  },
  listDocuments: (jobId: string) => api.get<ApplicationDocument[]>(`/jobs/${jobId}/documents`),
  getDocumentUrl: (jobId: string, documentId: string) =>
    api.get<string>(`/jobs/${jobId}/documents/${documentId}/file-url`),
  uploadDocuments: async (jobId: string, dto: UploadApplicationDocumentsDto) => {
    const form = new FormData();
    dto.files.forEach((file) => form.append('files', file));
    return api.postForm<ApplicationDocument[]>(`/jobs/${jobId}/documents`, form);
  },
};
